import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FeatureErrorBoundary } from '@/core/error-boundaries/public';
import { sentryReporter } from '@/core/observability/public';
import { Button } from '@/shared/components/ui/button';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { FormFieldInput } from '@/shared/components/custom/form-field/FormFieldInput';
import { useForgotPassword } from '../hooks/useForgotPassword';
import { createForgotPasswordSchema, type ForgotPasswordFormValues } from '../schemas';

export function ForgotPasswordPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { sendResetEmail, isPending, isSuccess, error } = useForgotPassword();

  const { control, handleSubmit, getValues } = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(createForgotPasswordSchema(t)),
    defaultValues: { email: '' },
  });

  const onSubmit = ({ email }: ForgotPasswordFormValues) => {
    sendResetEmail(email);
  };

  useEffect(() => {
    if (isSuccess) {
      navigate('/verify-recovery-code', { state: { email: getValues('email') } });
    }
  }, [isSuccess, navigate, getValues]);

  return (
    <FeatureErrorBoundary reporter={sentryReporter}>
      <Card className="w-full border-0 shadow-none bg-transparent">
        <CardHeader className="text-center">
          <CardTitle className="text-base font-medium">{t('forgotPassword.title')}</CardTitle>
        </CardHeader>

        <CardContent className="space-y-4 px-0">
          {error && (
            <Alert variant="destructive" data-testid="auth-forgot-error-alert">
              <AlertDescription>{error.message}</AlertDescription>
            </Alert>
          )}

          <form id="forgot-password-form" onSubmit={handleSubmit(onSubmit)}>
            <FormFieldInput
              control={control}
              name="email"
              label={t('forgotPassword.email')}
              type="email"
              autoComplete="email"
              disabled={isPending}
              floatingLabel
              data-testid="auth-forgot-email-input"
            />
          </form>

          <div className="flex flex-col gap-4">
            <Button
              type="submit"
              form="forgot-password-form"
              className="w-full"
              disabled={isPending}
              aria-busy={isPending}
              data-testid="auth-forgot-submit"
            >
              {isPending ? t('forgotPassword.submitting') : t('forgotPassword.submit')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => navigate('/login')}
              data-testid="auth-forgot-back"
            >
              {t('forgotPassword.backToLogin')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </FeatureErrorBoundary>
  );
}
